/*
 * RIGHT PANEL COMPONENT
 * 
 * Container component for the right side panel with contextual content.
 * 
 * Features:
 * - Renders content based on activeTab (chat or editor)
 * - Uses CSS display:none to preserve content state when switching
 * - Each content type (Chat, KanbanEditor, etc.) provides its own header
 * - No tab bar - panel shows one thing at a time based on what opened it
 * 
 * Design Decision:
 * Tabs were removed in favor of a simpler contextual model:
 * - Navbar chat button -> opens Chat (with its own header)
 * - Kanban gear button -> opens Kanban Editor (with its own header)
 * - User clicks respective trigger to switch content
 * 
 * Props:
 * - onClose: Callback to close the panel
 * - onOpenFullPage: Callback to open chat in full-page mode
 *
 * Recent changes:
 * - July 30, 2026 - Keeps embedded content inside the mobile panel shrink boundary.
 */

import { FC } from 'react';
import { useChatContext } from '../../context/ChatContext';
import Chat from '../../pages/Chat';
import EditorContainer from './EditorContainer';

interface RightPanelProps {
  onClose: () => void;
  onOpenFullPage: () => void;
}

const RightPanel: FC<RightPanelProps> = ({ onClose, onOpenFullPage }) => {
  const { activeTab } = useChatContext();

  return (
    <div className="flex h-full min-w-0 max-w-full flex-col">
      {/* Content Area - uses display to preserve state */}
      <div className="min-w-0 max-w-full flex-1 overflow-hidden">
        {/* Chat Content */}
        <div
          className="h-full min-w-0 max-w-full"
          style={{ display: activeTab === 'chat' ? 'block' : 'none' }}
        >
          <Chat
            onClose={onClose}
            onOpenFullPage={onOpenFullPage}
            showSessionSidebar={false}
          />
        </div>

        {/* Editor Content */}
        <div
          className="h-full min-w-0 max-w-full"
          style={{ display: activeTab === 'editor' ? 'block' : 'none' }}
        >
          <EditorContainer onClose={onClose} />
        </div>
      </div>
    </div>
  );
};

export default RightPanel;
